import type { AluminiumAISampleComposition, SpecificationListItem } from 'api';
import type { ComponentProps } from 'react';

import { MaterialBreakdownItem } from '../../components/MaterialBreakdownItem/MaterialBreakdownItem';
import { formatAluAiLooseComposition } from '../formatAluAiLooseComposition/formatAluAiLooseComposition';

type CompositionItem = ComponentProps<typeof MaterialBreakdownItem>;

const shouldShowItem = (item: { highlight?: boolean } | null | undefined, weight: number, showZeroValue = false) => {
  if (!item) {
    return false;
  }
  if (showZeroValue) {
    return true;
  }
  return item.highlight === true || weight > 0;
};

export const buildAluAiLooseCompositionItems = (
  composition: AluminiumAISampleComposition,
  totalWeight: number,
  instructions?: SpecificationListItem[]
) => {
  const formatted = formatAluAiLooseComposition(composition, totalWeight, instructions);

  const targetAluminiumItems: CompositionItem[] = [];
  const nonTargetItems: CompositionItem[] = [];

  targetAluminiumItems.push({
    label: formatted.aluminium.label,
    secondaryValue: formatted.aluminium.percentage,
    value: formatted.aluminium.weight,
    variant: 'bold',
  });

  if (shouldShowItem(formatted.ubc, composition.ubc * totalWeight)) {
    targetAluminiumItems.push({
      highlight: formatted.ubc.highlight,
      label: formatted.ubc.label,
      secondaryValue: formatted.ubc.percentage,
      value: formatted.ubc.weight,
    });
  }

  if (shouldShowItem(formatted.foilAndTray, composition.foil_and_tray * totalWeight)) {
    targetAluminiumItems.push({
      highlight: formatted.foilAndTray.highlight,
      label: formatted.foilAndTray.label,
      secondaryValue: formatted.foilAndTray.percentage,
      value: formatted.foilAndTray.weight,
    });
  }

  if (shouldShowItem(formatted.foodCans, composition.food_can * totalWeight)) {
    targetAluminiumItems.push({
      highlight: formatted.foodCans.highlight,
      label: formatted.foodCans.label,
      secondaryValue: formatted.foodCans.percentage,
      value: formatted.foodCans.weight,
    });
  }

  if (shouldShowItem(formatted.aluminiumAerosol, composition.aluminium_aerosol * totalWeight)) {
    targetAluminiumItems.push({
      highlight: formatted.aluminiumAerosol.highlight,
      label: formatted.aluminiumAerosol.label,
      secondaryValue: formatted.aluminiumAerosol.percentage,
      value: formatted.aluminiumAerosol.weight,
    });
  }

  if (shouldShowItem(formatted.otherAluminium, composition.other_aluminium * totalWeight)) {
    targetAluminiumItems.push({
      highlight: formatted.otherAluminium.highlight,
      label: formatted.otherAluminium.label,
      secondaryValue: formatted.otherAluminium.percentage,
      value: formatted.otherAluminium.weight,
    });
  }

  nonTargetItems.push({
    label: formatted.nonAluminium.label,
    secondaryValue: formatted.nonAluminium.percentage,
    value: formatted.nonAluminium.weight,
    variant: 'bold',
  });

  if (shouldShowItem(formatted.otherMetal, composition.other_metal * totalWeight)) {
    nonTargetItems.push({
      highlight: formatted.otherMetal.highlight,
      label: formatted.otherMetal.label,
      secondaryValue: formatted.otherMetal.percentage,
      value: formatted.otherMetal.weight,
    });
  }

  if (shouldShowItem(formatted.nonMetal, composition.non_metal * totalWeight)) {
    nonTargetItems.push({
      highlight: formatted.nonMetal.highlight,
      label: formatted.nonMetal.label,
      secondaryValue: formatted.nonMetal.percentage,
      value: formatted.nonMetal.weight,
    });
  }

  const totalItem: CompositionItem = {
    label: formatted.total.label,
    secondaryValue: '100%',
    value: formatted.total.weight,
    variant: 'bold',
  };

  return {
    nonTargetSection: {
      items: nonTargetItems,
    },
    targetAluminiumSection: {
      items: targetAluminiumItems,
    },
    totalItem,
  };
};
